import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { getOrCreateDeviceId, clearDeviceId } from '@/utils/deviceId'
import { generateWebSocketUrl, normalizeWebSocketUrl } from '@/utils/websocketUrl'

export interface UserInfo {
  id?: number
  userId?: string | number
  username?: string
  nickname?: string
  email?: string
  name?: string
  tel?: string
  phone?: string
  avatar?: string
  state?: string // 1-正常 0-禁用
  isAdmin?: string // 1-管理员 0-普通用户
  totalDevice?: number
  aliveNumber?: number
  totalMessage?: number
  loginTime?: string
  loginIp?: string
  createTime?: string
  registerSource?: string
}

export interface WebSocketConfig {
  url: string
  deviceId?: string
  deviceName?: string
  autoConnect: boolean
  robotId?: string
  robotName?: string
  /** 是否启用多连接模式（文本和二进制分开） */
  multiConnection?: boolean
}

export const useUserStore = defineStore('user', () => {
  const userInfo = useStorage<UserInfo | null>('userInfo', null, localStorage, {
    serializer: {
      read: (v: string) => {
        try {
          return v ? JSON.parse(v) as UserInfo : null
        } catch (e) {
          console.error('Failed to parse user info:', e)
          return null
        }
      },
      write: (v: UserInfo | null) => JSON.stringify(v),
    },
  })

  // Token 管理
  const token = useStorage<string>('token', '', localStorage)

  // WebSocket 配置管理
  const isDev = import.meta.env.DEV
  const defaultWsConfig: WebSocketConfig = {
    url: generateWebSocketUrl(isDev),
    deviceId: getOrCreateDeviceId(null, isDev), // 使用生成的设备ID
    deviceName: 'Web用户',
    autoConnect: true,
    multiConnection: false
  }

  const wsConfig = useStorage<WebSocketConfig>(
    'wsConfig',
    defaultWsConfig,
    localStorage,
    {
      serializer: {
        read: (v: string) => {
          try {
            const config = v ? JSON.parse(v) as WebSocketConfig : defaultWsConfig
            config.url = normalizeWebSocketUrl(config.url, isDev)
            return config
          } catch {
            return defaultWsConfig
          }
        },
        write: (v: WebSocketConfig) => JSON.stringify(v),
      },
    }
  )

  const navigationStyle = useStorage<'tabs' | 'sidebar'>('navigationStyle', 'tabs', localStorage)
  const isMobile = ref(false)

  const setUserInfo = (info: UserInfo) => {
    userInfo.value = info
  }

  const setMobileType = (mobile: boolean) => {
    isMobile.value = mobile
  }

  const setNavigationStyle = (style: 'tabs' | 'sidebar') => {
    navigationStyle.value = style
  }

  const clearUserInfo = () => {
    userInfo.value = null
    // 清除设备ID
    clearDeviceId()
  }

  const updateUserInfo = (info: Partial<UserInfo>) => {
    if (userInfo.value) {
      userInfo.value = { ...userInfo.value, ...info }
    }
  }

  const setToken = (newToken: string) => {
    token.value = newToken
  }

  const clearToken = () => {
    token.value = ''
    // 清除设备ID
    clearDeviceId()
  }

  const updateWsConfig = (config: Partial<WebSocketConfig>) => {
    wsConfig.value = { ...wsConfig.value, ...config }
  }

  /**
   * 获取或生成设备ID
   */
  const getDeviceId = (): string => {
    // 如果配置中已有deviceId，使用配置的
    if (wsConfig.value.deviceId) {
      return wsConfig.value.deviceId
    }

    // 否则生成新的
    const isDev = import.meta.env.DEV
    const deviceId = getOrCreateDeviceId(userInfo.value, isDev)

    // 更新配置
    wsConfig.value.deviceId = deviceId

    return deviceId
  }

  return {
    userInfo,
    token,
    wsConfig,
    isMobile,
    navigationStyle,
    setUserInfo,
    setMobileType,
    setNavigationStyle,
    clearUserInfo,
    updateUserInfo,
    setToken,
    clearToken,
    updateWsConfig,
    getDeviceId,
  }
})
